import React, { useMemo } from 'react';
import { Avatar, Box, Card, CardContent, Stack, Typography } from '@mui/material';
import { StaffPerformanceMeta, StaffPerformanceRecord, StaffSuccessUpdates, SuccessUpdatesMeta } from '../types';
import { formatDisplayName, shortLocationName } from '../utils/metrics';
import { buildDetailTiles } from '../utils/tiles';
import { ClientMovementCard } from './ClientMovementCard';
import { ClientUpdatesCard } from './ClientUpdatesCard';
import { ConversionCard } from './ConversionCard';
import { EmptyState } from './EmptyState';
import { KpiTiles } from './KpiTile';
import { SessionsChart } from './SessionsChart';
import { StaffPerformanceHeader } from './StaffPerformanceHeader';
import { CategoryFilterProps } from './ServiceCategorySelect';

export interface StaffDetailViewProps {
  record: StaffPerformanceRecord;
  meta: StaffPerformanceMeta | null;
  /** Present when a manager or admin drilled in from the team overview. */
  backTo?: { to: string; label: string };
  /**
   * This person's overdue client-update figures, passed only once they are ready (loaded, with a successful run
   * behind them). `updates` is `null` for a coach who leads no active clients. Omitted, the page is the plain
   * three-tile view.
   */
  successUpdates?: { updates: StaffSuccessUpdates | null; meta: SuccessUpdatesMeta };
  /** Service-category filter; `record.sessions` is already restricted to the selection. */
  categoryFilter?: CategoryFilterProps;
}

/**
 * One staff member's page: header, person row, three KPI tiles (a fourth for overdue client updates once they are
 * ready), then the sessions, client movement and conversion cards. With the updates, the client list closes the page,
 * for a coach on their own page and for a manager or admin drilling in alike.
 * @example <StaffDetailView record={record} meta={meta} />
 */
export const StaffDetailView: React.FC<StaffDetailViewProps> = ({ record, meta, backTo, successUpdates, categoryFilter }) => {
  const updatesReady = successUpdates !== undefined;
  const updatesDoc = successUpdates?.updates ?? null;
  const tiles = useMemo(() => buildDetailTiles(record, updatesReady ? updatesDoc : undefined), [record, updatesReady, updatesDoc]);
  const name = formatDisplayName(record);
  const updatesCard = updatesDoc ? <ClientUpdatesCard updates={updatesDoc} /> : null;

  return (
    <Box>
      <StaffPerformanceHeader
        subtitle={backTo ? `${name} · last 6 months vs the previous 6` : 'Your last 6 months vs the previous 6'}
        periods={record.periods}
        meta={meta}
        backTo={backTo}
        updatesCheckedAt={successUpdates?.meta.last_success_at}
        updatesFailed={successUpdates?.meta.status === 'error'}
      />

      <Stack direction="row" spacing={1.75} alignItems="center" sx={{ mb: 3 }}>
        <Avatar sx={{ bgcolor: 'primary.main', width: 48, height: 48, fontWeight: 600 }}>{name.charAt(0).toUpperCase()}</Avatar>
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
            {name}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Coach · {shortLocationName(record.home_location)} · Staff ID {record.staff_id}
          </Typography>
        </Box>
      </Stack>

      <Box sx={{ mb: 3 }}>
        <KpiTiles tiles={tiles} />
      </Box>

      <Stack spacing={3}>
        {record.sessions ? (
          <SessionsChart sessions={record.sessions} periods={record.periods} categoryFilter={categoryFilter} />
        ) : (
          <Card>
            <CardContent>
              <EmptyState title="No sessions recorded" body="No session data for this period." />
            </CardContent>
          </Card>
        )}
        <ClientMovementCard clients={record.clients} periods={record.periods} />
        <ConversionCard conversion={record.conversion} periods={record.periods} sourceCreatedDate={record.source_created_date} />
        {updatesCard}
      </Stack>
    </Box>
  );
};
